<template>
    <div>
        <p>
            Mode
            <b-select v-model="mode" :options="[
                { text: 'Normal — tous les fichiers vers un projet',        value: 'normal' },
                { text: 'Fichiers — dispatcher des fichiers via un CSV',    value: 'fichiers' },
                { text: 'CSV — dispatcher des lignes CSV vers des projets', value: 'csv' },
            ]" />
            <small class="text-muted d-block mt-1">
                <span v-if="mode === 'normal'">Les fichiers en entrée sont envoyés au projet fils sélectionné.</span>
                <span v-else-if="mode === 'fichiers'">Un CSV en entrée liste les fichiers à envoyer ; chaque ligne pointe vers un projet et/ou un fichier.</span>
                <span v-else>Un CSV en entrée est découpé ligne par ligne ; chaque groupe de lignes part vers son projet sous forme de CSV.</span>
            </small>
        </p>

        <p>
            Tag du projet
            <b-input v-model="tags" placeholder="ex: prod" />
        </p>

        <div v-if="mode === 'fichiers'">
            <p>
                Colonne du projet
                <small class="text-muted d-block mb-1">Colonne contenant l'UUID ou le libellé du projet fils</small>
                <b-input :list="'proj_col_project_' + _uid" v-model="column_project" placeholder="ex: projet_uid" />
                <datalist :id="'proj_col_project_' + _uid">
                    <option v-for="(c, i) in columns_data" :key="'ccp' + i">{{ c }}</option>
                </datalist>
            </p>
            <p>
                Colonne du nom de fichier
                <small class="text-muted d-block mb-1">Colonne contenant le nom du fichier à récupérer depuis S3</small>
                <b-input :list="'proj_col_file_' + _uid" v-model="column_file" placeholder="ex: nom_fichier" />
                <datalist :id="'proj_col_file_' + _uid">
                    <option v-for="(c, i) in columns_data" :key="'ccf' + i">{{ c }}</option>
                </datalist>
            </p>
            <p>
                Colonne de l'URL du fichier
                <small class="text-muted d-block mb-1">URL directe du fichier (optionnel — remplace la colonne nom de fichier)</small>
                <b-input :list="'proj_col_url_' + _uid" v-model="column_url" placeholder="ex: url_fichier" />
                <datalist :id="'proj_col_url_' + _uid">
                    <option v-for="(c, i) in columns_data" :key="'ccu' + i">{{ c }}</option>
                </datalist>
            </p>
            <p>
                Colonne des tags
                <small class="text-muted d-block mb-1">Colonne contenant le tag à appliquer (optionnel)</small>
                <b-input :list="'proj_col_tags_f_' + _uid" v-model="column_tags" placeholder="ex: tag" />
                <datalist :id="'proj_col_tags_f_' + _uid">
                    <option v-for="(c, i) in columns_data" :key="'cctf' + i">{{ c }}</option>
                </datalist>
            </p>
        </div>

        <div v-if="mode === 'csv'">
            <p>
                Colonne du projet
                <small class="text-muted d-block mb-1">Colonne contenant l'UUID ou le libellé du projet fils (optionnel — fallback sur le projet sélectionné)</small>
                <b-input :list="'proj_col_project_csv_' + _uid" v-model="column_project" placeholder="ex: projet_uid" />
                <datalist :id="'proj_col_project_csv_' + _uid">
                    <option v-for="(c, i) in columns_data" :key="'ccpc' + i">{{ c }}</option>
                </datalist>
            </p>
            <p>
                Colonne des tags
                <small class="text-muted d-block mb-1">Colonne contenant le tag à appliquer à chaque ligne (optionnel)</small>
                <b-input :list="'proj_col_tags_csv_' + _uid" v-model="column_tags" placeholder="ex: tag" />
                <datalist :id="'proj_col_tags_csv_' + _uid">
                    <option v-for="(c, i) in columns_data" :key="'cctc' + i">{{ c }}</option>
                </datalist>
            </p>
        </div>

        <b-button variant="link" class="p-0 mb-2 text-muted" @click="show_options = !show_options">
            Options avancées <i :class="show_options ? 'fas fa-minus' : 'fas fa-plus'"></i>
        </b-button>
        <b-collapse v-model="show_options">
            <div class="p-2 border rounded" style="background:#f8f9fa;">
                <p>
                    Nombre de fichiers par traitement
                    <small class="text-muted d-block mb-1">Divise les fichiers en lots de N avant d'appeler le projet fils</small>
                    <b-input v-model.number="nb_by_lot" type="number" placeholder="illimité" />
                </p>
                <p>
                    Poids des fichiers par traitement (Mo)
                    <small class="text-muted d-block mb-1">Divise les fichiers en lots selon leur poids total</small>
                    <b-input v-model.number="size_max" type="number" placeholder="illimité" />
                </p>
                <p>
                    <b-checkbox v-model="sequential">Exécuter les traitements un par un (séquentiel)</b-checkbox>
                </p>
                <div class="mt-2">
                    <strong>Documentation — projets pouvant être appelés</strong>
                    <small class="text-muted d-block mb-1">Liste des UUID de projets fils attendus (usage documentaire uniquement)</small>
                    <b-table-simple small bordered class="mt-1">
                        <b-thead>
                            <b-tr><b-th>UUID du projet</b-th><b-th></b-th></b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr v-for="(val, idx) in doc_project_possibilities" :key="'dpp' + idx">
                                <b-td><b-input v-model="doc_project_possibilities[idx]" size="sm" placeholder="uuid..." /></b-td>
                                <b-td><b-button size="sm" variant="danger" @click="doc_project_possibilities.splice(idx, 1)">x</b-button></b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                    <b-button size="sm" @click="doc_project_possibilities.push('')">+ Ajouter</b-button>
                </div>
            </div>
        </b-collapse>
    </div>
</template>

<script>
    export default {
        name: "projet",
        props: {
            data:          { default: () => [] },
            files:         { default: () => [] },
            client:        { default: null },
            size:          { default: 1000 },
            step_position: { default: 0 },
            project_id:    { default: "" },
        },
        watch: {
            data: {
                deep: true,
                immediate: true,
                handler() {
                    if (this.data && this.data.length > 0)
                        this.columns_data = Object.keys(this.data[0]);
                }
            },
        },
        data() {
            return {
                mode:                      "normal",
                tags:                      "",
                column_project:            "",
                column_tags:               "",
                column_file:               "",
                column_url:                "",
                nb_by_lot:                 null,
                size_max:                  null,
                sequential:                false,
                doc_project_possibilities: [],
                params:                    {},
                columns_data:              [],
                show_options:              false,
            };
        },
        methods: {
            save() {
                return {
                    project:                   this.project_id || "",
                    tags:                      this.tags || "",
                    params:                    this.params || {},
                    csv:                       this.mode === "csv",
                    column_file:               this.mode !== "normal" ? (this.column_file    || "") : "",
                    column_url:                this.mode !== "normal" ? (this.column_url     || "") : "",
                    column_project:            this.mode !== "normal" ? (this.column_project || "") : "",
                    column_tags:               this.mode !== "normal" ? (this.column_tags    || "") : "",
                    doc_project_possibilities: this.doc_project_possibilities || [],
                    nb_by_lot:                 this.nb_by_lot  || null,
                    size_max:                  this.size_max   || null,
                    sequential:                this.sequential || false,
                };
            },
            load(data) {
                if (!data) return;
                this.tags                      = data.tags                      || "";
                this.params                    = data.params                    || {};
                this.column_file               = data.column_file               || "";
                this.column_url                = data.column_url                || "";
                this.column_project            = data.column_project            || "";
                this.column_tags               = data.column_tags               || "";
                this.doc_project_possibilities = data.doc_project_possibilities || [];
                this.nb_by_lot                 = data.nb_by_lot                 || null;
                this.size_max                  = data.size_max                  || null;
                this.sequential                = data.sequential                || false;
                if (data.csv)
                    this.mode = "csv";
                else if (data.column_project)
                    this.mode = "fichiers";
                else
                    this.mode = "normal";
            },
        },
    }
</script>
